// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Group, Loader, Paper, Select, Stack, Table, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { MedplumClient } from '@medplum/core';
import { formatHumanName, getReferenceString, normalizeErrorString } from '@medplum/core';
import type { Practitioner, PractitionerRole, ProjectMembership } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCheck } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { POLARIS_PROVIDER_ROLE_SYSTEM, POLARIS_PROVIDER_ROLES } from '../../constants/appointmentTypes';
import { ROLES } from '../../utils/permissions';
import { withTenantScope } from '../../utils/tenant';

/** Display label for the RCM role — not in POLARIS_PROVIDER_ROLES (scheduling-only lib). */
const RCM_DISPLAY = 'Clinical Ops / RCM';

const ROLE_OPTIONS = [
  { value: '', label: '(none)' },
  { value: ROLES.PHYSICIAN, label: POLARIS_PROVIDER_ROLES.physician.display },
  { value: ROLES.NURSE_PRACTITIONER, label: POLARIS_PROVIDER_ROLES['nurse-practitioner'].display },
  { value: ROLES.CARE_COORDINATOR, label: POLARIS_PROVIDER_ROLES['care-coordinator'].display },
  { value: ROLES.SOFTWARE_ENGINEER, label: POLARIS_PROVIDER_ROLES['software-engineer'].display },
  { value: ROLES.CLINICAL_OPS_RCM, label: RCM_DISPLAY },
];

const ROLE_LABELS: Record<string, string> = {
  [ROLES.PHYSICIAN]: POLARIS_PROVIDER_ROLES.physician.display,
  [ROLES.NURSE_PRACTITIONER]: POLARIS_PROVIDER_ROLES['nurse-practitioner'].display,
  [ROLES.CARE_COORDINATOR]: POLARIS_PROVIDER_ROLES['care-coordinator'].display,
  [ROLES.SOFTWARE_ENGINEER]: POLARIS_PROVIDER_ROLES['software-engineer'].display,
  [ROLES.CLINICAL_OPS_RCM]: RCM_DISPLAY,
};

/**
 * Look up the AccessPolicy for the given role code by the naming convention
 * `<role>-access-policy` and rewrite every ProjectMembership for the practitioner
 * to point at the new policy, preserving existing `parameter` entries (which carry
 * the per-org scoping that must not be clobbered).
 *
 * This is Option A — universal across all role changes, closing the pre-existing
 * gap where physician→care-coordinator left the old AccessPolicy attached.
 */
async function swapAccessPolicy(medplum: MedplumClient, practitionerId: string, roleCode: string): Promise<void> {
  const policyName = `${roleCode}-access-policy`;
  const accessPolicy = await medplum.searchOne('AccessPolicy', { name: policyName });
  if (!accessPolicy?.id) {
    console.warn(`[ProviderRolePage] AccessPolicy "${policyName}" not found — skipping policy swap`);
    return;
  }
  const newPolicyRef = { reference: `AccessPolicy/${accessPolicy.id}` };

  const memberships: ProjectMembership[] = await medplum.searchResources('ProjectMembership', {
    'profile-type': 'Practitioner',
    profile: `Practitioner/${practitionerId}`,
  });

  for (const membership of memberships) {
    const existingAccess = membership.access ?? [];
    const updatedAccess: ProjectMembership['access'] =
      existingAccess.length > 0
        ? existingAccess.map((entry) => ({ ...entry, policy: newPolicyRef }))
        : [{ policy: newPolicyRef }];

    await medplum.updateResource<ProjectMembership>({
      ...membership,
      access: updatedAccess,
    });
  }
}

interface ProviderRow {
  practitioner: Practitioner;
  practitionerRole: PractitionerRole | undefined;
  currentRoleCode: string;
}

export function ProviderRolePage(): JSX.Element {
  const medplum = useMedplum();
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    async function load(): Promise<void> {
      const practitioners = await medplum.searchResources('Practitioner', { _count: '100', _sort: 'name' });
      const practitionerRoles = await medplum.searchResources('PractitionerRole', { _count: '200' });

      const roleByPractitioner = new Map<string, PractitionerRole>();
      for (const pr of practitionerRoles) {
        const ref = pr.practitioner?.reference;
        if (ref) {
          roleByPractitioner.set(ref, pr);
        }
      }

      const rows: ProviderRow[] = practitioners.map((p) => {
        const ref = getReferenceString(p);
        const pr = roleByPractitioner.get(ref);
        const coding = pr?.code?.[0]?.coding?.find((c) => c.system === POLARIS_PROVIDER_ROLE_SYSTEM);
        return {
          practitioner: p,
          practitionerRole: pr,
          currentRoleCode: coding?.code ?? '',
        };
      });

      setProviders(rows);
    }

    load()
      .catch((err) => {
        console.error('[ProviderRolePage] Failed to load providers:', err);
      })
      .finally(() => setLoading(false));
  }, [medplum]);

  const handleRoleChange = useCallback(
    async (practitionerIndex: number, newRoleCode: string | null) => {
      const row = providers[practitionerIndex];
      if (!row) {
        return;
      }

      const roleCode = newRoleCode ?? '';
      if (roleCode === row.currentRoleCode) {
        return;
      }

      const practitionerRef = getReferenceString(row.practitioner);
      setSavingId(practitionerRef ?? null);

      try {
        if (!roleCode) {
          // Clear role: delete PractitionerRole if it exists
          if (row.practitionerRole?.id) {
            await medplum.deleteResource('PractitionerRole', row.practitionerRole.id);
          }
          setProviders((prev) =>
            prev.map((p, i) =>
              i === practitionerIndex ? { ...p, practitionerRole: undefined, currentRoleCode: '' } : p
            )
          );
        } else {
          // Build the coding payload. For known scheduling roles, use POLARIS_PROVIDER_ROLES for
          // the canonical display string. For RCM (not in that map), use RCM_DISPLAY directly.
          const knownRole = POLARIS_PROVIDER_ROLES[roleCode as keyof typeof POLARIS_PROVIDER_ROLES];
          const codingPayload = [
            {
              coding: [
                {
                  system: POLARIS_PROVIDER_ROLE_SYSTEM,
                  code: roleCode,
                  display: knownRole?.display ?? ROLE_LABELS[roleCode] ?? roleCode,
                },
              ],
            },
          ];

          let updated: PractitionerRole;
          if (row.practitionerRole) {
            updated = await medplum.updateResource<PractitionerRole>({
              ...row.practitionerRole,
              code: codingPayload,
            });
          } else {
            updated = await medplum.createResource<PractitionerRole>(
              withTenantScope({
                resourceType: 'PractitionerRole',
                practitioner: { reference: practitionerRef },
                active: true,
                code: codingPayload,
              })
            );
          }

          // Swap AccessPolicy on every ProjectMembership for this practitioner.
          // This closes the pre-existing gap where any role change left the old policy attached.
          const practitionerId = row.practitioner.id;
          if (practitionerId) {
            await swapAccessPolicy(medplum, practitionerId, roleCode);
          }

          setProviders((prev) =>
            prev.map((p, i) =>
              i === practitionerIndex ? { ...p, practitionerRole: updated, currentRoleCode: roleCode } : p
            )
          );
        }

        showNotification({
          color: 'green',
          icon: <IconCheck size={16} />,
          message: `Role updated for ${formatHumanName(row.practitioner.name?.[0])}`,
        });
      } catch (error) {
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      } finally {
        setSavingId(null);
      }
    },
    [medplum, providers]
  );

  if (loading) {
    return (
      <Paper p="xl" mx="auto" mt="xl">
        <Group justify="center">
          <Loader />
        </Group>
      </Paper>
    );
  }

  return (
    <Paper p="xl" mx="auto" mt="xl" withBorder radius="md" shadow="sm">
      <Stack gap="md">
        <Title order={3}>Manage Provider Roles</Title>
        <Text size="sm" c="dimmed">
          Assign or update provider roles. Roles determine feature access and scheduling eligibility.
        </Text>

        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Provider</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Current Role</Table.Th>
              <Table.Th>Update Role</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {providers.map((row, index) => {
              const name = row.practitioner.name?.[0]
                ? formatHumanName(row.practitioner.name[0])
                : (row.practitioner.id ?? 'Unknown');
              const email = row.practitioner.telecom?.find((t) => t.system === 'email')?.value ?? '';
              const isSaving = savingId === getReferenceString(row.practitioner);

              return (
                <Table.Tr key={row.practitioner.id}>
                  <Table.Td>{name}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {row.currentRoleCode ? (
                      <Badge variant="light">{ROLE_LABELS[row.currentRoleCode] ?? row.currentRoleCode}</Badge>
                    ) : (
                      <Text size="sm" c="dimmed">
                        None
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      w={220}
                      data={ROLE_OPTIONS}
                      value={row.currentRoleCode}
                      onChange={(val) => {
                        handleRoleChange(index, val).catch(console.error);
                      }}
                      disabled={isSaving}
                      rightSection={isSaving ? <Loader size={14} /> : undefined}
                    />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Stack>
    </Paper>
  );
}
